import React, { useState, useEffect, useContext } from "react";

import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import IconButton from "@material-ui/core/IconButton";
import MenuItem from "@material-ui/core/MenuItem";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import Autocomplete, { createFilterOptions } from "@material-ui/lab/Autocomplete";
import Alert from "@material-ui/lab/Alert";
import CloseIcon from "@material-ui/icons/Close";
import PersonAddOutlinedIcon from "@material-ui/icons/PersonAddOutlined";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";

import api from "../../services/api";
import ContactModal from "../ContactModal";
import ContactAvatar from "../ContactAvatar";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import formatPhone from "../../helpers/formatPhone";
import sidebarScrollbar from "../../helpers/sidebarScrollbar";

const CONNECTION_STATUS = {
  CONNECTED: { label: "Conectada", color: "#43A047" },
  OPENING: { label: "Conectando", color: "#FB8C00" },
  PAIRING: { label: "Conectando", color: "#FB8C00" },
  qrcode: { label: "Aguardando QR Code", color: "#FB8C00" },
  TIMEOUT: { label: "Sem resposta", color: "#E53935" },
  DISCONNECTED: { label: "Desconectada", color: "#E53935" },
};
const connectionStatus = (status) =>
  CONNECTION_STATUS[status] || { label: status || "Desconhecido", color: "#9E9E9E" };

const useStyles = makeStyles((theme) => ({
  paper: {
    borderRadius: 16,
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    padding: theme.spacing(3, 3, 2.5),
  },
  headerText: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
    lineHeight: 1.3,
  },
  subtitle: {
    marginTop: 4,
    color: theme.palette.text.secondary,
  },
  closeButton: {
    marginTop: -6,
    marginRight: -8,
    color: theme.palette.text.secondary,
  },
  content: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    padding: theme.spacing(2.5, 3, 3),
    ...sidebarScrollbar(theme),
  },
  contactCard: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.action.hover,
  },
  contactName: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  muted: {
    color: theme.palette.text.secondary,
    fontSize: 13,
  },
  placeholder: {
    color: theme.palette.text.secondary,
  },
  option: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    width: "100%",
    minWidth: 0,
  },
  optionText: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  addIcon: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.action.selected,
    flexShrink: 0,
  },
  connectionItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    width: "100%",
  },
  statusText: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    display: "inline-block",
    flexShrink: 0,
  },
  alert: {
    marginTop: theme.spacing(1),
    borderRadius: 10,
  },
  actions: {
    padding: theme.spacing(2, 3),
    gap: theme.spacing(1),
  },
  actionButton: {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 10,
    height: 40,
    padding: theme.spacing(0, 2.5),
  },
  cancelButton: {
    color: theme.palette.text.primary,
  },
  buttonProgress: {
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

const filter = createFilterOptions({
  trim: true,
});

const NewTicketModal = ({ modalOpen, onClose, initialContact }) => {
  const classes = useStyles();
  const [options, setOptions] = useState([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState("");
  const [selectedWhatsapp, setSelectedWhatsapp] = useState("");
  const [newContact, setNewContact] = useState({});
  const [whatsapps, setWhatsapps] = useState([]);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  // atendimento já aberto com outro usuário (o backend recusa criar outro)
  const [conflict, setConflict] = useState(null);
  const { user } = useContext(AuthContext);
  const { companyId, whatsappId } = user;
  const queues = user.queues || [];
  const isAdmin = user.profile === "admin";
  const hasFixedContact = initialContact !== undefined && initialContact?.id !== undefined;

  useEffect(() => {
    if (initialContact?.id !== undefined) {
      setOptions([initialContact]);
      setSelectedContact(initialContact);
    }
  }, [initialContact]);

  useEffect(() => {
    api
      .get(`/whatsapp`, { params: { companyId, session: 0 } })
      .then(({ data }) => setWhatsapps(data || []))
      .catch(() => {});

    if (whatsappId !== null && whatsappId !== undefined) {
      setSelectedWhatsapp(whatsappId);
    }

    if (queues.length === 1) {
      setSelectedQueue(queues[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!modalOpen || searchParam.length < 3) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("contacts", {
            params: { searchParam },
          });
          setOptions(data.contacts);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, modalOpen]);

  const handleClose = () => {
    onClose();
    setSearchParam("");
    setConflict(null);
    setSelectedContact(null);
  };

  const handleSaveTicket = async (contactId) => {
    if (!contactId) return;
    if (selectedQueue === "" && !isAdmin) {
      toast.error("Selecione um departamento");
      return;
    }

    setSaving(true);
    setConflict(null);
    try {
      const queueId = selectedQueue !== "" ? selectedQueue : null;
      const whatsappId = selectedWhatsapp !== "" ? selectedWhatsapp : null;
      const { data: ticket } = await api.post("/tickets", {
        contactId: contactId,
        queueId,
        whatsappId,
        userId: user.id,
        status: "open",
      });

      onClose(ticket);
    } catch (err) {
      let conflictingTicket = null;
      try {
        conflictingTicket = JSON.parse(err.response?.data?.error);
      } catch {
        conflictingTicket = null;
      }

      if (conflictingTicket?.userId && conflictingTicket.userId !== user?.id) {
        setConflict({
          userName: conflictingTicket.user?.name,
          queueName: conflictingTicket.queue?.name,
        });
      } else if (conflictingTicket?.userId) {
        onClose(conflictingTicket);
      } else {
        toastError(err);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSelectOption = (e, newValue) => {
    setConflict(null);
    if (!newValue) {
      setSelectedContact(null);
    } else if (newValue.id) {
      // contato existente (com ou sem número)
      setSelectedContact(newValue);
    } else if (newValue.name) {
      setNewContact({ name: newValue.name });
      setContactModalOpen(true);
    }
  };

  const handleCloseContactModal = () => {
    setContactModalOpen(false);
  };

  const handleAddNewContactTicket = (contact) => {
    handleSaveTicket(contact.id);
  };

  const createAddContactOption = (filterOptions, params) => {
    const filtered = filter(filterOptions, params);
    if (params.inputValue !== "" && !loading && searchParam.length >= 3) {
      filtered.push({
        name: `${params.inputValue}`,
        isNew: true,
      });
    }
    return filtered;
  };

  const renderOption = (option) => {
    if (option.isNew) {
      return (
        <div className={classes.option}>
          <span className={classes.addIcon}>
            <PersonAddOutlinedIcon fontSize="small" />
          </span>
          <div className={classes.optionText}>
            <span className={classes.contactName}>Cadastrar “{option.name}”</span>
            <span className={classes.muted}>Novo contato</span>
          </div>
        </div>
      );
    }
    return (
      <div className={classes.option}>
        <ContactAvatar
          src={option.profilePicUrl}
          name={option.name}
          style={{ width: 32, height: 32, fontSize: 14 }}
        />
        <div className={classes.optionText}>
          <span className={classes.contactName}>{option.name}</span>
          <span className={classes.muted}>
            {option.number ? formatPhone(option.number) : "Sem WhatsApp"}
          </span>
        </div>
      </div>
    );
  };

  const renderOptionLabel = (option) => {
    if (typeof option === "string") return option;
    if (option.number) {
      return `${option.name} - ${formatPhone(option.number)}`;
    }
    return `${option.name || ""}`;
  };

  const contactWithoutNumber = Boolean(selectedContact && !selectedContact.number);
  const selectedConnection = whatsapps.find((w) => w.id === selectedWhatsapp);
  const connectionOffline =
    selectedConnection && selectedConnection.status !== "CONNECTED";
  const canSave = Boolean(selectedContact) && !contactWithoutNumber && !saving;

  return (
    <>
      <ContactModal
        open={contactModalOpen}
        initialValues={newContact}
        onClose={handleCloseContactModal}
        onSave={handleAddNewContactTicket}
      ></ContactModal>
      <Dialog
        open={modalOpen}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        classes={{ paper: classes.paper }}
        aria-labelledby="new-ticket-title"
      >
        <div className={classes.header}>
          <div className={classes.headerText}>
            <Typography id="new-ticket-title" component="h2" className={classes.title}>
              Novo atendimento
            </Typography>
            <Typography variant="body2" className={classes.subtitle}>
              Escolha o departamento e a conexão para iniciar a conversa.
            </Typography>
          </div>
          <IconButton
            className={classes.closeButton}
            onClick={handleClose}
            aria-label="Fechar"
            disabled={saving}
          >
            <CloseIcon />
          </IconButton>
        </div>

        <DialogContent dividers className={classes.content}>
          {/* CONTATO */}
          {hasFixedContact ? (
            <div className={classes.contactCard}>
              <ContactAvatar
                src={initialContact.profilePicUrl}
                name={initialContact.name}
                style={{ width: 40, height: 40 }}
              />
              <div className={classes.optionText}>
                <span className={classes.contactName}>{initialContact.name}</span>
                <span className={classes.muted}>
                  {initialContact.number ? formatPhone(initialContact.number) : "Sem WhatsApp"}
                </span>
              </div>
            </div>
          ) : (
            <Autocomplete
              fullWidth
              options={options}
              loading={loading}
              clearOnBlur
              autoHighlight
              freeSolo
              clearOnEscape
              getOptionLabel={renderOptionLabel}
              renderOption={renderOption}
              getOptionDisabled={(option) => !option.isNew && !option.number}
              filterOptions={createAddContactOption}
              onChange={handleSelectOption}
              noOptionsText="Nenhum contato encontrado"
              loadingText="Buscando…"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Contato"
                  placeholder="Digite nome ou número (mín. 3 letras)"
                  variant="outlined"
                  margin="dense"
                  autoFocus
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => setSearchParam(e.target.value)}
                  onKeyPress={(e) => {
                    if (!canSave) return;
                    if (e.key === "Enter") {
                      handleSaveTicket(selectedContact.id);
                    }
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {loading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
            />
          )}

          {/* DEPARTAMENTO */}
          <TextField
            select
            fullWidth
            variant="outlined"
            margin="dense"
            label={isAdmin ? "Departamento" : "Departamento *"}
            value={selectedQueue}
            onChange={(e) => setSelectedQueue(e.target.value)}
            InputLabelProps={{ shrink: true }}
            SelectProps={{
              displayEmpty: true,
              MenuProps: {
                anchorOrigin: { vertical: "bottom", horizontal: "left" },
                transformOrigin: { vertical: "top", horizontal: "left" },
                getContentAnchorEl: null,
              },
            }}
            helperText={
              queues.length === 0
                ? "Você não está em nenhum departamento."
                : isAdmin
                ? "Opcional para administradores."
                : undefined
            }
          >
            <MenuItem value="" disabled={!isAdmin}>
              <span className={classes.placeholder}>
                {isAdmin ? "Sem departamento" : "Selecione um departamento"}
              </span>
            </MenuItem>
            {queues.map((queue) => (
              <MenuItem key={queue.id} value={queue.id}>
                <span
                  className={classes.statusDot}
                  style={{ backgroundColor: queue.color || "#9E9E9E", marginRight: 10 }}
                />
                {queue.name}
              </MenuItem>
            ))}
          </TextField>

          {/* CONEXÃO */}
          <TextField
            select
            fullWidth
            variant="outlined"
            margin="dense"
            label="Conexão"
            value={selectedWhatsapp}
            onChange={(e) => setSelectedWhatsapp(e.target.value)}
            InputLabelProps={{ shrink: true }}
            SelectProps={{
              displayEmpty: true,
              MenuProps: {
                anchorOrigin: { vertical: "bottom", horizontal: "left" },
                transformOrigin: { vertical: "top", horizontal: "left" },
                getContentAnchorEl: null,
              },
              renderValue: (value) => {
                if (value === "") {
                  return <span className={classes.placeholder}>Conexão padrão</span>;
                }
                const whatsapp = whatsapps.find((w) => w.id === value);
                if (!whatsapp) return "";
                const status = connectionStatus(whatsapp.status);
                return (
                  <span className={classes.connectionItem}>
                    <span>{whatsapp.name}</span>
                    <span className={classes.statusText}>
                      <span className={classes.statusDot} style={{ backgroundColor: status.color }} />
                      {status.label}
                    </span>
                  </span>
                );
              },
            }}
            helperText={
              connectionOffline
                ? "Esta conexão não está conectada; a mensagem pode não ser enviada."
                : undefined
            }
          >
            <MenuItem value="">
              <span className={classes.placeholder}>Conexão padrão</span>
            </MenuItem>
            {whatsapps.map((whatsapp) => {
              const status = connectionStatus(whatsapp.status);
              return (
                <MenuItem key={whatsapp.id} value={whatsapp.id}>
                  <span className={classes.connectionItem}>
                    <span>{whatsapp.name}</span>
                    <span className={classes.statusText}>
                      <span className={classes.statusDot} style={{ backgroundColor: status.color }} />
                      {status.label}
                    </span>
                  </span>
                </MenuItem>
              );
            })}
          </TextField>

          {contactWithoutNumber && (
            <Alert severity="info" className={classes.alert}>
              Este contato não tem número de WhatsApp. Edite o contato e adicione um número para
              iniciar a conversa.
            </Alert>
          )}

          {conflict && (
            <Alert severity="warning" className={classes.alert}>
              Este contato já está em atendimento
              {conflict.userName ? (
                <>
                  {" "}com <strong>{conflict.userName}</strong>
                </>
              ) : null}
              {conflict.queueName ? (
                <>
                  {" "}no departamento <strong>{conflict.queueName}</strong>
                </>
              ) : null}
              . Peça a transferência para continuar a conversa.
            </Alert>
          )}
        </DialogContent>

        <DialogActions className={classes.actions}>
          <Button
            onClick={handleClose}
            disabled={saving}
            className={`${classes.actionButton} ${classes.cancelButton}`}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            disableElevation
            disabled={!canSave}
            onClick={() => handleSaveTicket(selectedContact.id)}
            className={classes.actionButton}
            style={{ position: "relative" }}
          >
            Iniciar conversa
            {saving && <CircularProgress size={24} className={classes.buttonProgress} />}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
export default NewTicketModal;
